import React, { FC, useCallback, useEffect, useState } from 'react';
import { trackEvent } from '../../../extra/sharedMethods';
import { updateUser } from '../../../scripts/apis';
import { AnalyticsEvent } from '../../../scripts/constants/analytics-event';
import { useUserId, useUserSafe } from '../../../scripts/hooks';
import { logError } from '../../../scripts/utils';
import { QADivider } from './QADivider';
import { QAInspiration } from './QAInspiration';
import { QARecommendedApps } from './QARecommendedApps';
import { QASuggestedQueries } from './QASuggestedQueries';
import { QAUseCases } from './QAUseCases';

export const QAOnboardingMessage: FC<{
  shouldStream: boolean;
}> = ({ shouldStream }) => {
  const userId = useUserId();
  const hasUserSeenOnboarding = useUserSafe(
    (user) => user.hasSeenWebOnboarding
  );

  const [currentChild, setCurrentChild] = useState(0);

  const handleChildReady = useCallback((timer: number) => {
    setTimeout(() => {
      setCurrentChild((prevChild) => prevChild + 1);
    }, timer);
  }, []);

  const handleOnboardingCompleted = useCallback(() => {
    if (!hasUserSeenOnboarding) {
      updateUser(userId, { has_seen_web_onboarding: true }).catch((error) => {
        logError(error);
      });
    }
  }, [hasUserSeenOnboarding, userId]);

  useEffect(() => {
    if (!shouldStream) {
      setCurrentChild(99);
    }
  }, [shouldStream]);

  useEffect(() => {
    trackEvent(AnalyticsEvent.QAOnboardingMessageShown);
    handleChildReady(1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return handleOnboardingCompleted;
  }, [handleOnboardingCompleted]);

  return (
    <div className="mb-16 flex flex-col gap-8">
      {currentChild >= 1 && (
        <QAUseCases
          onReadyToShowNext={handleChildReady}
          shouldStream={shouldStream}
        />
      )}
      {currentChild >= 2 && (
        <QAInspiration
          onReadyToShowNext={handleChildReady}
          shouldStream={shouldStream}
        />
      )}
      {currentChild >= 3 && (
        <QADivider
          onReadyToShowNext={handleChildReady}
          shouldStream={shouldStream}
          text="Setup"
        />
      )}
      {currentChild >= 4 && (
        <QARecommendedApps
          onReadyToShowNext={handleChildReady}
          shouldStream={shouldStream}
        />
      )}
      {currentChild >= 5 && (
        <QADivider
          onReadyToShowNext={handleChildReady}
          shouldStream={shouldStream}
          text="Ask a question"
        />
      )}
      {currentChild >= 6 && (
        <QASuggestedQueries
          onLastItemCompleted={handleOnboardingCompleted}
          shouldStream={shouldStream}
        />
      )}
    </div>
  );
};
